import { useCallback, useEffect, useRef } from 'react'
import { EditorContent, useEditor, type Editor, type JSONContent } from '@tiptap/react'
import { BlockGutter } from './BlockGutter'
import { extensions } from './extensions'
import { cleanPastedHtml } from './paste'
import { Toolbar } from './Toolbar'
import { LinkTrigger } from './LinkTrigger'
import { SlashMenu } from './SlashMenu'
import { imageFilesFrom, UploadProvider, type Uploader } from './upload'
import type { State } from '../api'

/**
 * The default editing surface: blocks, not Markdown.
 *
 * The document shape is `docSchema` from @staleless/markdown, so what this
 * produces converts to Markdown through the property-tested bridge. The browser
 * never serialises Markdown itself — it posts the document and the server does
 * it, which is why the block editor, the Markdown tab and the MCP all write
 * byte-identical files.
 */
interface Props {
  doc: JSONContent | null
  readOnly: boolean
  /**
   * The site's pages, so a link can be made by naming one — the toolbar's
   * Link bar and the `[[` trigger both read it. Optional: an editor drawn
   * without it keeps the typed-address bar it always had.
   */
  siteState?: State
  branch?: string | null
  onChange: (doc: JSONContent) => void
  onSave: () => void
  /**
   * Uploads a file and returns what to reference it by.
   *
   * Absent on a read-only branch, which is also why paste and drop fall back to
   * the default behaviour rather than reporting a failure: there is nothing
   * wrong, this branch simply cannot be written to.
   */
  upload?: Uploader
  /** Hand a selection to the page's conversation. */
  onComment?: (selection: { quote: string; prefix: string; suffix: string }) => void
  /**
   * What is selected, reported as it changes, for §22's help with the words.
   *
   * The RANGE travels with the content because the two must not drift: the
   * suggestion replaces the range that was sent, and by the time it arrives the
   * writer's focus is in the panel. `text` is the plain words, which is all a
   * bar needs to decide whether to appear; converting the content to Markdown
   * costs a module load and happens only when somebody asks for help.
   */
  onSelect?: (
    selection: { from: number; to: number; content: JSONContent[]; text: string } | null,
  ) => void
  /**
   * A handle for putting a suggestion back, given once the editor exists.
   *
   * `replaceRange` is the whole surface: the blocks go exactly where the
   * selection was, through TipTap's own history so one undo takes it back.
   */
  onReady?: (handle: {
    replaceRange: (from: number, to: number, content: JSONContent[]) => void
  }) => void
}

export function BlockEditor({
  doc,
  readOnly,
  siteState,
  branch,
  onChange,
  onSave,
  upload,
  onComment,
  onSelect,
  onReady,
}: Props) {
  /**
   * Put a screenshot where the cursor is.
   *
   * A figure rather than a bare image, because a figure carries alt text and
   * warns when it is missing. Inserting a bare image would make the fastest path
   * to adding a screenshot also the one that produces an inaccessible page.
   *
   * Insertion happens after the upload resolves, and ProseMirror's handlers are
   * synchronous — so this consumes the event and finishes later. The position is
   * captured first: the person may well have carried on typing, and appending at
   * wherever the cursor has since wandered is worse than appending where they
   * asked for it.
   */
  const insertUploads = useCallback(
    (files: File[], at?: number) => {
      if (!upload || files.length === 0) return
      void (async () => {
        for (const file of files) {
          const result = await upload(file)
          if (!result) return
          editorRef.current
            ?.chain()
            .focus()
            .insertContentAt(at ?? editorRef.current.state.selection.to, {
              type: 'figure',
              attrs: { src: result.url, alt: result.altText, width: null },
            })
            .run()
        }
      })()
    },
    [upload],
  )

  /** The scrolling frame, which the block gutter positions itself inside. */
  const scroller = useRef<HTMLDivElement | null>(null)
  const editorRef = useRef<Editor | null>(null)
  /** The last document this editor produced, for the reconciliation below. */
  const emitted = useRef<JSONContent | null>(null)

  const editor = useEditor(
    {
      extensions,
      editable: !readOnly,
      content: doc ?? { type: 'doc', content: [{ type: 'paragraph' }] },
      editorProps: {
        attributes: { class: 'be' },
        /**
         * Word and Google Docs paste HTML wrapped around a private layout
         * language, and the parts of it this editor cannot keep were taking
         * content with them — a pasted table arrived as one empty table per cell
         * with the text loose underneath. See `cleanPastedHtml`.
         */
        transformPastedHTML: (html) => cleanPastedHtml(html),
        handlePaste: (_view, event) => {
          const files = imageFilesFrom(event.clipboardData)
          if (files.length === 0 || !upload) return false
          event.preventDefault()
          insertUploads(files)
          return true
        },
        handleDrop: (view, event, _slice, moved) => {
          // `moved` is a drag WITHIN the document — text being reordered. Taking
          // that over would turn moving a paragraph into an upload attempt.
          if (moved) return false
          const files = imageFilesFrom(event.dataTransfer)
          if (files.length === 0 || !upload) return false
          event.preventDefault()
          // Drop where the pointer is, not where the cursor was left.
          const at = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos
          insertUploads(files, at)
          return true
        },
        handleKeyDown: (_view, event) => {
          if ((event.metaKey || event.ctrlKey) && event.key === 's') {
            event.preventDefault()
            onSave()
            return true
          }
          return false
        },
      },
      onSelectionUpdate: ({ editor }) => {
        if (!onSelect) return
        const { from, to, empty } = editor.state.selection
        if (empty) {
          onSelect(null)
          return
        }
        const slice = editor.state.doc.slice(from, to)
        onSelect({
          from,
          to,
          content: (slice.content.toJSON() as JSONContent[] | null) ?? [],
          text: editor.state.doc.textBetween(from, to, '\n\n'),
        })
      },
      onUpdate: ({ editor }) => {
        const next = editor.getJSON()
        // Remembered by identity, so the effect below can tell this document
        // apart from one that arrived from somewhere else.
        emitted.current = next
        onChange(next)
      },
    },
    [readOnly],
  )

  /**
   * Replacing the document when another page is opened.
   *
   * `false` keeps it out of the undo history, so Cmd+Z cannot undo across a page
   * change.
   *
   * The identity check is the important part. Every keystroke sends a document
   * up and gets it back as a new `doc` prop, so this effect runs constantly. If
   * a second keystroke lands before React commits the first, `doc` is one change
   * behind — the JSON no longer matches, and this used to "reconcile" by
   * REVERTING the change just made. Typing quickly lost characters, and choosing
   * a block from the slash menu inserted nothing perhaps a third of the time,
   * with no error anywhere.
   *
   * So a document this editor emitted is never applied back to it. Only one that
   * genuinely came from elsewhere — opening a page, discarding an edit,
   * switching branch — is.
   */
  useEffect(() => {
    if (!editor || !doc) return
    if (doc === emitted.current) return
    const current = JSON.stringify(editor.getJSON())
    if (current === JSON.stringify(doc)) return
    editor.commands.setContent(doc, { emitUpdate: false })
  }, [doc, editor])

  useEffect(() => {
    /**
     * The second argument is not optional in spirit.
     *
     * `setEditable` emits an update by default, and TipTap means it: the
     * listener fires with a document nobody touched. Opening a page flips this
     * from read-only to editable, so every page open reported an edit, autosave
     * wrote the untouched document back, and the page appeared in review having
     * been looked at rather than changed. It also raced anything that had
     * changed the file since the load, and won.
     *
     * Nothing changed, so nothing is announced.
     */
    editor?.setEditable(!readOnly, false)
  }, [editor, readOnly])

  // A ref as well as the value, so the async insertion above reaches the CURRENT
  // editor rather than the one that existed when the handler was created.
  useEffect(() => {
    editorRef.current = editor
  }, [editor])

  useEffect(() => {
    if (!editor || !onReady) return
    onReady({
      replaceRange: (from, to, content) => {
        editor.chain().focus().insertContentAt({ from, to }, content).run()
      },
    })
  }, [editor, onReady])

  if (!editor) return <div className="empty">Loading the editor…</div>

  return (
    // The provider wraps the content, so a node view can offer a file picker
    // without being handed a callback it would only pass along.
    <UploadProvider value={upload ?? null}>
      <div className="be-host">
        <Toolbar
          editor={editor}
          disabled={readOnly}
          {...(onComment ? { onComment } : {})}
          {...(siteState ? { state: siteState, branch: branch ?? null } : {})}
        />
        {/*
          The gutter is positioned inside this frame, so it scrolls with the
          document rather than floating over a page that has moved under it.
        */}
        <div className="be-scroll" ref={scroller}>
          <EditorContent editor={editor} />
          <BlockGutter
            editor={editor}
            frame={scroller}
            readOnly={readOnly}
            {...(onComment ? { onComment } : {})}
          />
        </div>
        {!readOnly && <SlashMenu editor={editor} />}
        {!readOnly && siteState && (
          <LinkTrigger editor={editor} state={siteState} branch={branch ?? null} />
        )}
      </div>
    </UploadProvider>
  )
}
